import { DarkHours, ThemePreference } from '@utils/theme/themePreference';

import { UserSettings } from './types';

type Appearance = Pick<UserSettings, 'theme' | 'darkHours' | 'colorScheme'>;

/** What this browser has: its light or dark (light if never chosen), any hours it was given, its colour scheme. */
export type BrowserAppearance = {
  theme: ThemePreference;
  darkHours: DarkHours | null;
  colorScheme: string;
};

/**
 * Light or dark and the colour scheme moved from each browser onto the account on 2026-10-08 (Petr: they belong there,
 * like calendar styling). An account saved before then has neither, so the browser it is next opened in gives it its
 * own: what the student chose there, or what they had been seeing, light and the default scheme. The same fills them
 * back in should anything save the settings without them.
 */
export const appearanceToCopy = (account: Partial<UserSettings>, browser: BrowserAppearance): Appearance => {
  const copy: Appearance = {};
  if (!account.theme) {
    copy.theme = browser.theme;
    if (browser.darkHours && !account.darkHours) copy.darkHours = browser.darkHours;
  }
  if (!account.colorScheme) copy.colorScheme = browser.colorScheme;
  return copy;
};

/**
 * A new account starts on Auto, following the device (Petr, 2026-10-08), with the colour scheme its browser has. It is
 * new when its settings are first saved: an account from before then already has some.
 */
export const newAccountAppearance = (browser: BrowserAppearance): Appearance => ({
  theme: 'auto',
  colorScheme: browser.colorScheme
});

/**
 * Soft white became the default on 2026-10-11 (Petr: White is harsh beside it, and Sky's blue undertone harsher), and
 * accounts that had White picked moved to it, once. The account notes that the move is done, and so does every
 * background picked from then on, so a student who picks White again keeps it. Any other background is left alone; an
 * account with none gets Soft white anyway, as the default.
 */
export const softWhiteSwitch = (account: Partial<UserSettings>): Pick<UserSettings, 'background' | 'softWhiteSwitched'> =>
  account.background === 'white' && !account.softWhiteSwitched ? { background: 'ivory', softWhiteSwitched: true } : {};
